import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import mixpanelService from '../services/mixpanel'

export const useMeasurements = (poolId) => {
  const [measurements, setMeasurements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMeasurements = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (!poolId) {
        setMeasurements([])
        return
      }

      const { data, error } = await supabase
        .from('measurements')
        .select('*')
        .eq('pool_id', poolId)
        .order('measured_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setMeasurements(data || [])
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [poolId])

  const createMeasurement = async (measurementData) => {
    try {
      const { data, error } = await supabase
        .from('measurements')
        .insert([
          {
            ...measurementData,
            pool_id: poolId,
            total_chlorine: (measurementData.free_chlorine || 0) + (measurementData.combined_chlorine || 0),
          },
        ])
        .select()

      if (error) throw error
      const created = data?.[0]
      if (created) {
        setMeasurements([created, ...measurements])
      }

      // Track measurement creation (isolated so analytics failures don't affect the result)
      try {
        const parametersCount = Object.keys(measurementData).filter(key =>
          measurementData[key] !== null &&
          measurementData[key] !== undefined &&
          measurementData[key] !== ''
        ).length

        mixpanelService.trackFunnelStep('add_measurement', {
          measurement_id: created?.id,
          pool_id: poolId,
          parameters_count: parametersCount,
          has_notes: !!measurementData.notes
        })
      } catch (trackingError) {
        console.warn('Mixpanel tracking failed:', trackingError)
      }

      return { data: created, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const updateMeasurement = async (measurementId, measurementData) => {
    try {
      const updatedData = {
        ...measurementData,
        total_chlorine: (measurementData.free_chlorine || 0) + (measurementData.combined_chlorine || 0),
      }

      const { data, error } = await supabase
        .from('measurements')
        .update(updatedData)
        .eq('id', measurementId)
        .eq('pool_id', poolId)
        .select()

      if (error) throw error
      const updated = data?.[0]
      if (updated) {
        setMeasurements(measurements.map(m => m.id === measurementId ? updated : m))
      }
      return { data: updated, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const deleteMeasurement = async (measurementId) => {
    try {
      const { error } = await supabase
        .from('measurements')
        .delete()
        .eq('id', measurementId)
        .eq('pool_id', poolId)

      if (error) throw error
      setMeasurements(measurements.filter(m => m.id !== measurementId))
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const getLatestMeasurement = () => {
    return measurements.length > 0 ? measurements[0] : null
  }

  const getRecentMeasurements = (limit = 5) => {
    return measurements.slice(0, limit)
  }

  useEffect(() => {
    fetchMeasurements()
  }, [poolId, fetchMeasurements])

  return {
    measurements,
    loading,
    error,
    createMeasurement,
    updateMeasurement,
    deleteMeasurement,
    getLatestMeasurement,
    getRecentMeasurements,
    refetch: fetchMeasurements,
  }
}